import {
  FetchResult,
  MutationFunctionOptions,
  OperationVariables,
} from '@apollo/client';
import { Center, Stack } from '@chakra-ui/react';
import { DottedProgressBar, DottedProgressBarItem } from '@gamma/ui/display';
import {
  ConfigureAndEnableIdentityProviderMutation,
  ConfigureAndEnableIdentityProviderMutationVariables,
} from '@investigator/shared/generated/graphql';
import { i18n } from '@investigator/shared/localization';
import { Dispatch, SetStateAction } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import {
  ModalSteps,
  storeIdentityProviderProps,
} from '../IdpConfigurationModal';
import { ConfigureIDP } from './ConfigureIDP';
import { ConfigureInvestigator } from './ConfigureInvestigator';
import { ConfigureRoles } from './ConfigureRoles';

export type MutationFunctionType<R = unknown, V = OperationVariables> = (
  // for backward compatibility, remove when possible
  options?: MutationFunctionOptions<R, V>,
) => Promise<FetchResult<R>>;

interface ModalBodyContentProps {
  step: number;
  xmlFile: File[];
  setXmlFile: Dispatch<SetStateAction<File[]>>;
  fileError: string;
  setFileError: Dispatch<SetStateAction<string>>;
  useFormProps: UseFormReturn<FieldValues, unknown>;
  resetModalForm: () => void;
  showAlert: string;
  configureIdentityProvider: MutationFunctionType<
    ConfigureAndEnableIdentityProviderMutation,
    ConfigureAndEnableIdentityProviderMutationVariables
  >;
  updateIdentityProvider: MutationFunctionType;
  storeIdentityProviderData: (input: storeIdentityProviderProps) => void;
  getStoredIdentityProviderData: () => storeIdentityProviderProps;
  data: OperationVariables | undefined;
  isUpdating: boolean;
  useIDPUploadXMLHandlerProps: {
    uploadFile: (files: File[]) => Promise<File[]>;
    autoFill: (data: string | Record<string, string>) => void;
  };
  parsedData: Record<string, string> | undefined;
  setParsedData: Dispatch<SetStateAction<Record<string, string> | undefined>>;
  updateIDPConfig: boolean;
  setUpdateIDPConfig: Dispatch<SetStateAction<boolean>>;
}

type ModalBodyProps = ModalBodyContentProps;

const ModalBodyContent = ({
  step,
  setFileError,
  xmlFile,
  setXmlFile,
  configureIdentityProvider,
  updateIdentityProvider,
  storeIdentityProviderData,
  getStoredIdentityProviderData,
  useFormProps,
  resetModalForm,
  showAlert,
  fileError,
  data,
  isUpdating,
  useIDPUploadXMLHandlerProps,
  parsedData,
  setParsedData,
  updateIDPConfig,
  setUpdateIDPConfig,
}: ModalBodyContentProps) => {
  switch (step) {
    case ModalSteps.configureIDP:
      return <ConfigureIDP />;
    case ModalSteps.configureInvestigator:
      return (
        <ConfigureInvestigator
          storeIdentityProviderData={storeIdentityProviderData}
          useFormProps={useFormProps}
          xmlFile={xmlFile}
          showAlert={showAlert}
          fileError={fileError}
          setFileError={setFileError}
          setXmlFile={setXmlFile}
          resetModalForm={resetModalForm}
          data={data}
          isUpdating={isUpdating}
          useIDPUploadXMLHandlerProps={useIDPUploadXMLHandlerProps}
          parsedData={parsedData}
          setParsedData={setParsedData}
          setUpdateIDPConfig={setUpdateIDPConfig}
        />
      );
    case ModalSteps.configureRoles:
      return (
        <ConfigureRoles
          data={data}
          useFormProps={useFormProps as UseFormReturn<FieldValues, object>}
          configureIdentityProvider={configureIdentityProvider}
          updateIdentityProvider={updateIdentityProvider}
          getStoredIdentityProviderData={getStoredIdentityProviderData}
          isUpdating={isUpdating}
          updateIDPConfig={updateIDPConfig}
          setUpdateIDPConfig={setUpdateIDPConfig}
        />
      );
    default:
      return null;
  }
};

export const ModalBody = ({
  step,
  setFileError,
  xmlFile,
  setXmlFile,
  useFormProps,
  resetModalForm,
  showAlert,
  configureIdentityProvider,
  updateIdentityProvider,
  storeIdentityProviderData,
  getStoredIdentityProviderData,
  fileError,
  data,
  isUpdating,
  useIDPUploadXMLHandlerProps,
  parsedData,
  setParsedData,
  updateIDPConfig,
  setUpdateIDPConfig,
}: ModalBodyProps) => {
  return (
    <Stack spacing={6}>
      <Center>
        <DottedProgressBar activeStep={step}>
          <DottedProgressBarItem stepNumber={1}>
            {i18n.pages.access.idpConfigurationModal.progressSteps.configureIdp}
          </DottedProgressBarItem>
          <DottedProgressBarItem stepNumber={2}>
            {
              i18n.pages.access.idpConfigurationModal.progressSteps
                .configureInvestigator
            }
          </DottedProgressBarItem>
          <DottedProgressBarItem stepNumber={3}>
            {
              i18n.pages.access.idpConfigurationModal.progressSteps
                .configureRoles
            }
          </DottedProgressBarItem>
        </DottedProgressBar>
      </Center>
      <ModalBodyContent
        configureIdentityProvider={configureIdentityProvider}
        updateIdentityProvider={updateIdentityProvider}
        storeIdentityProviderData={storeIdentityProviderData}
        getStoredIdentityProviderData={getStoredIdentityProviderData}
        step={step}
        setFileError={setFileError}
        xmlFile={xmlFile}
        setXmlFile={setXmlFile}
        useFormProps={useFormProps}
        resetModalForm={resetModalForm}
        showAlert={showAlert}
        fileError={fileError}
        data={data}
        isUpdating={isUpdating}
        useIDPUploadXMLHandlerProps={useIDPUploadXMLHandlerProps}
        parsedData={parsedData}
        setParsedData={setParsedData}
        updateIDPConfig={updateIDPConfig}
        setUpdateIDPConfig={setUpdateIDPConfig}
      />
    </Stack>
  );
};
